/**
 * Pointer-tracked metallic sheen on CTAs — light-field motion model. Full
 * rationale (motion model provenance, selector hole, hardening notes) is in
 * docs/CLAUDE-HANDOFF.md under "CTA sheen hardening" — keep this file's
 * comments as short pointers, not a restatement.
 *
 * The oversized light field (`::after` in critical.css) translates on the
 * compositor via --field-x / --field-y rather than repainting a gradient
 * origin per event. markOutlined runs at idle; a control hovered first
 * marks itself on demand. measure() only re-runs on pointerenter or when
 * scroll/resize sets `dirty`; pointercancel mirrors pointerleave;
 * canTrack/reducedMotion `change` listeners stop/reset or rebind mid-session
 * (activeControls, enableTracking, disableTracking).
 */
const SELECTOR = [
  // __cta / __ghost / __primary / __secondary / __submit: safe substring,
  // "__" never appears in an unrelated class here.
  ":where(a, button)[class*='__cta']",
  ":where(a, button)[class*='__ghost']",
  ":where(a, button)[class*='__primary']",
  ":where(a, button)[class*='__secondary']",
  ":where(a, button)[class*='__submit']",
  // -cta / -ghost (feature-cta, mobile-cta/-ghost): the actual selector
  // hole — see docs/CLAUDE-HANDOFF.md for why .text-cta forces the 3-class
  // exclusion below and why -primary/-secondary/-submit are omitted.
  // SYNC WITH critical.css's "CTA LIGHT FIELD" selector list.
  ":where(a, button)[class*='-cta']:not(.site-header__tertiary, .site-header__mobile-tertiary, .site-header__mobile-link, .site-header__logout, .site-header__mobile-logout)",
  ":where(a, button)[class*='-ghost']",
].join(',');

const canTrack = window.matchMedia('(hover: hover) and (pointer: fine)');
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

// Recovered verbatim from assets/cta-light-track.js @
// f50e49de2de5de0138a58b0d74f34e2b1b49c9e6^ — don't retune without checking.
const FOLLOW_FACTOR = 0.46; // exponential follow applied per 16.67ms frame
const FRAME_MS = 16.67;
const MAX_FRAME_ELAPSED_MS = 48; // clamp long frames (tab-switch, jank)
const SETTLE_THRESHOLD = 0.05; // stop the rAF loop once within this of target, both axes
// X is the recovered value above. Y is deliberately lower, not restored
// symmetry: a ~250x50 pill has little vertical travel, and the 112deg band
// already reads vertical motion as part of the sweep. Don't "fix" this.
const AXIS_CLAMP_X = 22;
const AXIS_CLAMP_Y = 9;
const FIELD_SCALE = 2.4; // field is inset: -70%, i.e. 240% of the control

/** @typedef {{ x: number, y: number }} FieldOffset */

// Cleanup callbacks per bound control, drained by disableTracking so a
// mid-session Reduce Motion toggle fully unbinds and re-enabling rebinds
// cleanly.
/** @type {Map<HTMLElement, () => void>} */
const activeControls = new Map();

// Marks a control's fill state so CSS can accent-fill an outlined control on
// hover. Guards on attribute PRESENCE — it's always '' once set (falsy), so
// a truthy check would never skip a repeat call.
/** @param {HTMLElement} el */
function markOne(el) {
  if (el.dataset.ctaSheen !== undefined) return;

  // UNDERLINED TEXT LINKS ARE NOT CONTROLS IN THIS SYSTEM — they opt out here,
  // before anything is stamped on them, and that is the whole fix for a bug
  // that was only half fixed the first time.
  //
  // critical.css already cancelled the sheen pseudo-element for
  // [data-cta-underline]. It could not cancel what happens BELOW: an
  // underlined link has a transparent background, so it was classified as an
  // OUTLINED control and stamped data-cta-outline, and
  // `[data-cta-outline]:hover` fills with --color-accent. Joey, 2026-08-25:
  // "hovering link cta filling here, wrong." — a solid teal rectangle behind
  // "See full testing data".
  //
  // Skipping the whole function is right rather than skipping only the outline
  // stamp: there is nothing for the rAF loop to move on a control with no
  // light field, so binding one would be wasted work on every hover.
  if (el.dataset.ctaUnderline !== undefined) return;

  el.dataset.ctaSheen = '';
  const bg = getComputedStyle(el).backgroundColor;
  const transparent = bg === 'transparent' || /^rgba\(.*,\s*0\)$/.test(bg);
  if (transparent) el.dataset.ctaOutline = '';
}

// Idle bulk pass; a control hovered first marks itself via onPointerEnter,
// so a cold-load first hover is never wrong.
/** @param {Document | Element} root */
function markOutlined(root = document) {
  root.querySelectorAll(SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) markOne(el);
  });
}

// Binds the light-field translate to one control. Idempotent via
// data-cta-sheen-bound (presence check), so neither a section reload nor a
// matchMedia re-enable ever double-binds or starts a second rAF loop.
/** @param {HTMLElement} cta */
function bindTracking(cta) {
  if (cta.dataset.ctaSheenBound !== undefined) return;
  // The same opt-out as markOne, and it has to be repeated here rather than
  // relied on there: enableTracking() walks SELECTOR and binds directly, so a
  // guard in markOne alone would still leave an rAF loop translating a field
  // that critical.css has already set to `content: none`.
  if (cta.dataset.ctaUnderline !== undefined) return;
  cta.dataset.ctaSheenBound = '';

  let frame = 0;
  let lastFrame = 0;
  /** @type {FieldOffset} */
  let current = { x: 0, y: 0 };
  /** @type {FieldOffset} */
  let target = { x: 0, y: 0 };
  let centerX = 0;
  let centerY = 0;
  let fieldWidth = 1;
  let fieldHeight = 1;
  // Cleared on the next pointermove once re-measured; avoids a
  // forced-layout rect read on every move.
  let dirty = false;

  const measure = () => {
    const rect = cta.getBoundingClientRect();
    centerX = rect.left + rect.width / 2;
    centerY = rect.top + rect.height / 2;
    fieldWidth = Math.max(rect.width * FIELD_SCALE, 1);
    fieldHeight = Math.max(rect.height * FIELD_SCALE, 1);
  };

  const markDirty = () => {
    dirty = true;
  };

  /**
   * @param {PointerEvent} event
   * @returns {FieldOffset}
   */
  const targetFromPointer = (event) => ({
    x: Math.min(AXIS_CLAMP_X, Math.max(-AXIS_CLAMP_X, ((event.clientX - centerX) / fieldWidth) * 100)),
    y: Math.min(AXIS_CLAMP_Y, Math.max(-AXIS_CLAMP_Y, ((event.clientY - centerY) / fieldHeight) * 100)),
  });

  /** @param {FieldOffset} position */
  const paintField = (position) => {
    cta.style.setProperty('--field-x', `${position.x}%`);
    cta.style.setProperty('--field-y', `${position.y}%`);
  };

  /** @param {DOMHighResTimeStamp} timestamp */
  const paint = (timestamp) => {
    const elapsed = lastFrame ? Math.min(MAX_FRAME_ELAPSED_MS, timestamp - lastFrame) : FRAME_MS;
    const follow = 1 - Math.pow(1 - FOLLOW_FACTOR, elapsed / FRAME_MS);
    lastFrame = timestamp;
    const nextX = current.x + (target.x - current.x) * follow;
    const nextY = current.y + (target.y - current.y) * follow;
    current = { x: nextX, y: nextY };
    paintField(current);

    if (Math.abs(target.x - nextX) > SETTLE_THRESHOLD || Math.abs(target.y - nextY) > SETTLE_THRESHOLD) {
      frame = window.requestAnimationFrame(paint);
    } else {
      frame = 0;
      lastFrame = 0;
    }
  };

  const queuePaint = () => {
    if (!frame) frame = window.requestAnimationFrame(paint);
  };

  const stopLoop = () => {
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    lastFrame = 0;
  };

  // Attached on enter, removed on leave/cancel — not left on window.
  const attachDirtyWatchers = () => {
    window.addEventListener('scroll', markDirty, { capture: true, passive: true });
    window.addEventListener('resize', markDirty, { passive: true });
  };

  const detachDirtyWatchers = () => {
    window.removeEventListener('scroll', markDirty, true);
    window.removeEventListener('resize', markDirty);
  };

  /** @param {PointerEvent} event */
  const onPointerEnter = (event) => {
    markOne(cta);
    measure();
    dirty = false;
    current = targetFromPointer(event);
    target = current;
    paintField(current);
    attachDirtyWatchers();
  };

  /** @param {PointerEvent} event */
  const onPointerMove = (event) => {
    if (dirty) {
      measure();
      dirty = false;
    }
    target = targetFromPointer(event);
    queuePaint();
  };

  // Shared by pointerleave AND pointercancel, so a cancelled pointer can't
  // leave the field lit and off-centre.
  const onPointerLeave = () => {
    detachDirtyWatchers();
    dirty = false;
    target = { x: 0, y: 0 };
    queuePaint();
  };

  cta.addEventListener('pointerenter', onPointerEnter);
  cta.addEventListener('pointermove', onPointerMove);
  cta.addEventListener('pointerleave', onPointerLeave);
  cta.addEventListener('pointercancel', onPointerLeave);

  activeControls.set(cta, () => {
    cta.removeEventListener('pointerenter', onPointerEnter);
    cta.removeEventListener('pointermove', onPointerMove);
    cta.removeEventListener('pointerleave', onPointerLeave);
    cta.removeEventListener('pointercancel', onPointerLeave);
    detachDirtyWatchers();
    stopLoop();
    current = { x: 0, y: 0 };
    target = { x: 0, y: 0 };
    paintField(current);
    cta.removeAttribute('data-cta-sheen-bound');
  });
}

// Binds every eligible, connected control under root; a no-op unless both
// media queries favour the effect. Drops activeControls entries whose
// element left the document.
/** @param {Document | Element} root */
function enableTracking(root = document) {
  activeControls.forEach((cleanup, el) => {
    if (!el.isConnected) {
      cleanup();
      activeControls.delete(el);
    }
  });

  if (!canTrack.matches || reducedMotion.matches) return;
  root.querySelectorAll(SELECTOR).forEach((el) => {
    if (el instanceof HTMLElement) bindTracking(el);
  });
}

// Fully unbinds and resets every active control — Reduce Motion turning on
// mid-session must lose the effect immediately, not at next reload.
function disableTracking() {
  activeControls.forEach((cleanup) => cleanup());
  activeControls.clear();
}

function handleTrackingPreferenceChange() {
  if (canTrack.matches && !reducedMotion.matches) {
    enableTracking();
  } else {
    disableTracking();
  }
}

canTrack.addEventListener('change', handleTrackingPreferenceChange);
reducedMotion.addEventListener('change', handleTrackingPreferenceChange);

/** @param {Document | Element} root */
function init(root = document) {
  markOutlined(root);
  enableTracking(root);
}

if ('requestIdleCallback' in window) requestIdleCallback(() => init());
else init();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) init(event.target);
});

export {};
